import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import CommonTable from "components/ReactTable/Table";
import RightPanelCard from "components/Common/RightPanelCard";

import {
  customStyle,
  fixedWidthCustomStyle,
  REACT_APP_GOOGLE_MAPS_API_KEY,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";

import {
  getCustomerLocationList,
  addCustomerLocation,
  getCustomerLocationById,
  editCustomerLocation,
  deleteCustomerLocation,
  getRegions,
  getCities,
} from "actions/customerLocation";

import { showAlert } from "components/Common/commonFunctions";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";

const initialServiceField = {
  outlet: {
    value: "",
    error: "",
    isChanged: false,
  },
  region: {
    value: null,
    error: "",
    isChanged: false,
  },
  city: {
    value: null,
    error: "",
    isChanged: false,
  },
  mobile: {
    value: "",
    error: "",
    isChanged: false,
  },
  name: {
    value: "",
    error: "",
    isChanged: false,
  },
  email: {
    value: "",
    error: "",
    isChanged: false,
  },
  address: {
    value: "",
    error: "",
    isChanged: false,
  },
  alt_mobile: {
    value: "",
    error: "",
    isChanged: false,
  },
  alt_name: {
    value: "",
    error: "",
    isChanged: false,
  },
  alt_email: {
    value: "",
    error: "",
    isChanged: false,
  },
  mapDetails: {
    location_name: "",
    formattedAddress: "",
    placeID: "",
    locationType: "",
    coordinates: { lat: null, lng: null },
  },
  postCode: {
    value: "",
    error: "",
    isChanged: false,
  },
};

function ServiceLocations({
  customerId,
  notify,
  getValidationError,
  hasAnyError,
  activeTab,
}) {
  const defaultCenter = { lat: 20, lng: 77 };
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const dispatch = useDispatch();
  const debounceRef = useRef(null);
  const allLocations = useSelector((state) => state.customerLocationList);
  const regions = useSelector(
    (state) => state.customerLocationList.regions?.region || [],
  );
  const cities = useSelector(
    (state) => state.customerLocationList?.cities?.city || [],
  );
  const [locPageNum, setLocPageNum] = useState(1);
  const [locationFilters, setLocationFilters] = useState({
    searchText: "",
    region: null,
    city: null,
    filterParams: { filters: [], searchString: "" },
  });
  const [locationFormData, setLocationFormData] = useState(initialServiceField);
  const [map, setMap] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [modal, setModal] = useState({
    isOpen: false,
    mode: "",
    data: null,
  });
  const [stateMeta, setStateMeta] = useState({
    loader: false,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    if (!customerId || activeTab !== "serviceLocation") return;

    getRegions(dispatch);
  }, [customerId, activeTab]);

  //this useEffect fills store with data by calling api
  useEffect(() => {
    if (!customerId || activeTab !== "serviceLocation") return;

    const { filters, searchString } = locationFilters.filterParams;
    getCustomerLocationList(
      dispatch,
      customerId,
      locPageNum,
      filters,
      searchString,
    );
  }, [locPageNum, locationFilters.filterParams, customerId, activeTab]);

  //this useEffect gets data from store whenever that data changes
  useEffect(() => {
    const response = allLocations?.customerLocation;

    if (response?.code === 200) {
      setStateMeta({
        isLoading: false,
        error: "",
        noRecords: response?.data?.customerLocation?.length === 0,
      });
    } else if (response?.code || response?.message) {
      setStateMeta({
        isLoading: false,
        error:
          response?.message ||
          "Failed to load service locations. Please try again.",
        noRecords: false,
      });
    }
  }, [allLocations]);

  useEffect(() => {
    if (!isLoaded || !map || activeTab !== "serviceLocation") return;

    if (locationFormData.address.value) {
      geocodeAddress(locationFormData.address.value);
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const currentPos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setMarkerPosition(currentPos);
            map?.panTo(currentPos);
          },
          () => {
            setMarkerPosition(defaultCenter);
          },
        );
      } else {
        setMarkerPosition(defaultCenter);
      }
    }
  }, [isLoaded, map, activeTab]);

  const regionOptions = useMemo(() => {
    return regions.map((r) => ({
      value: r.ID,
      label: r.name,
    }));
  }, [regions]);

  const cityOptions = useMemo(() => {
    return cities.map((c) => ({
      value: c.ID,
      label: c.name,
    }));
  }, [cities]);

  const locationRecords =
    allLocations?.customerLocation?.data?.customerLocation || [];
  const totalLocationRecords =
    allLocations?.customerLocation?.data?.totalFilteredRecords || 0;

  const totalRecords = allLocations?.customerLocation?.data?.totalRecords || 0;

  const formattedLocationList = (locationRecords || []).map((item, index) => ({
    ...item,
    srNo: (locPageNum - 1) * DEFAULT_RECORDS_PER_PAGE + (index + 1),
  }));

  const buildLocationFilterParams = () => {
    const filterParams = [];

    if (locationFilters.region) {
      filterParams.push({
        field: "RegionID",
        condition: "eq",
        filterValues: [String(locationFilters.region.value)],
      });
    }

    return {
      filters: filterParams.length ? JSON.stringify(filterParams) : [],
      searchString: locationFilters.searchText || "",
    };
  };

  const fetchCitiesForRegion = (regionID) => {
    const cityFilter = JSON.stringify([
      {
        field: "RegionID",
        condition: "eq",
        filterValues: [String(regionID)],
      },
    ]);

    getCities(dispatch, cityFilter);
  };

  const handleSelectChange = (selectedOption, name) => {
    const value = selectedOption ? selectedOption.value : null;

    setLocationFormData((prev) => ({
      ...prev,
      [name]: {
        value,
        error: "",
      },
    }));

    if (name === "city") {
      const cityName = selectedOption?.label || "";

      setLocationFormData((prev) => ({
        ...prev,
        city: { ...prev.city, value, error: "" },
        address: {
          ...prev.address,
          value: cityName,
          error: "",
        },
      }));

      if (cityName) {
        geocodeAddress(cityName);
      }

      return;
    }

    if (name === "region") {
      setLocationFormData((prev) => ({
        ...prev,
        region: { ...prev.region, value, error: "" },
        city: { value: null, error: "" },
      }));

      if (value) {
        fetchCitiesForRegion(value);
      }
    }
  };

  const handleLocationSearch = () => {
    const newParams = buildLocationFilterParams();

    if (
      JSON.stringify(locationFilters.filterParams) !== JSON.stringify(newParams)
    ) {
      setLocationFilters((prev) => ({ ...prev, filterParams: newParams }));
      if (locPageNum !== 1) setLocPageNum(1);
      setStateMeta((prev) => ({ ...prev, showLoaderOnClick: true, isLoading: true, }))
    }
  };

  const handleLocationClear = () => {
    setLocationFilters({
      searchText: "",
      region: null,
      city: null,
      filterParams: { filters: [], searchString: "" },
    });
    setStateMeta((prev) => ({ ...prev, isLoading: true, }))

    if (locPageNum !== 1) setLocPageNum(1);
  };

  const handleOpenModal = (mode, data) => {
    setModal({ isOpen: true, mode, data });
    if (data) {
      const newData = {
        ...initialServiceField,
      };

      Object.keys(newData).forEach((key) => {
        if (key === "mapDetails") {
          newData.mapDetails =
            data.mapDetails ?? initialServiceField.mapDetails;
        } else {
          newData[key] = {
            ...initialServiceField[key],
            value: data[key] ?? "",
            error: "",
            isChanged: false,
          };
        }
      });

      newData.ID = data.ID;
      setLocationFormData(newData);

      if (data.mapDetails?.coordinates) {
        setMarkerPosition(data.mapDetails.coordinates);
      }
    } else {
      setLocationFormData(initialServiceField);
    }
  };

  const handleCloseModal = () => {
    setModal({ isOpen: false, mode: "", data: null });
    setLocationFormData(initialServiceField);
    setMarkerPosition(null);
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setLocationFormData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: value, error: "" },
    }));

    if (name === "address") {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (value.trim()) {
        debounceRef.current = setTimeout(() => {
          geocodeAddress(value.trim());
        }, 400);
      }
    }
  };

  const handleAddressKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = e.target.value;
      setLocationFormData((prev) => ({
        ...prev,
        address: { ...prev.address, value },
      }));
      geocodeAddress(value);
    }
  };

  const validateLocationFields = () => {
    let updated = { ...locationFormData };
    let isValid = true;

    Object.keys(updated).forEach((field) => {
      if (["alt_name", "alt_email", "alt_mobile"].includes(field)) return;

      const value = updated[field].value;
      const error = getValidationError(field, value);

      updated[field] = { ...updated[field], error };

      if (error) isValid = false;
    });

    setLocationFormData(updated);

    return isValid;
  };

  const handleLocationBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);

    setLocationFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const refreshLocationsAfterSave = async () => {
    const { filters, searchString } = locationFilters.filterParams;
    await getCustomerLocationList(
      dispatch,
      customerId,
      1,
      filters,
      searchString,
    );
    setLocPageNum(1);
  };

  const handleSaveLocation = async () => {
    const isValid = validateLocationFields();
    if (!isValid) return;

    const addr = locationFormData.address.value.trim();
    handleLocationBlur("address", addr);

    let geoDetails = locationFormData.mapDetails;

    if (addr && !geoDetails.placeID) {
      geoDetails = await geocodeAddress(addr);
      if (!geoDetails) {
        notify("danger", "Unable to fetch location from Google Maps.");
        return;
      }
    }

    setStateMeta({ loader: true, error: "" });

    const payload = {
      customerID: customerId,
      countryID: 1,
      regionID: locationFormData.region.value,
      cityID: locationFormData.city.value,
      outletName: locationFormData.outlet.value,
      address: locationFormData.address.value,
      contactName: locationFormData.name.value,
      ContactNo: locationFormData.mobile.value,
      email: locationFormData.email.value,
      alternateContactName: locationFormData.alt_name.value,
      alternateContactNo: locationFormData.alt_mobile.value,
      alternateEmail: locationFormData.alt_email.value,
      postCode: locationFormData.postCode.value,
      mapDetails: {
        location_name: locationFormData.outlet.value || "",
        formattedAddress: geoDetails.formattedAddress,
        placeID: geoDetails.placeID,
        locationType: geoDetails.locationType,
        coordinates: geoDetails.coordinates,
      },
    };

    const res = await addCustomerLocation(payload);

    if (res.code === 200) {
      notify("success", res?.message || "Location added successfully.");
      await refreshLocationsAfterSave();
      setStateMeta({ loader: false, error: "" });
      handleCloseModal();
    } else {
      setStateMeta({ loader: false, error: "" });
      notify(
        "danger",
        Array.isArray(res.message)
          ? res.message.map((item) => item.Msg).join(", ")
          : res.message || "Failed to save location",
      );
    }
  };

  const handleOpenEditLocation = async (location) => {
    setStateMeta({ loader: true, error: "" });

    const res = await getCustomerLocationById(customerId, location.ID);

    if (res.code === 200) {
      const loc = res.data;

      handleOpenModal("Edit", {
        ID: loc.ID,
        outlet: loc.outletName,
        address: loc.address,
        region: loc.region?.ID,
        city: loc.city?.ID,
        name: loc.contactName,
        email: loc.email,
        mobile: loc.contactNo,
        alt_name: loc.alternateContactName,
        alt_email: loc.alternateEmail,
        alt_mobile: loc.alternateContactNo,
        mapDetails: loc.mapDetails,
        postCode: loc.postCode,
      });

      fetchCitiesForRegion(loc?.region?.ID);

      setStateMeta({ loader: false, error: "" });
    } else {
      notify("danger", res.message || "Failed to load location details.");
      setStateMeta({ loader: false, error: "" });
    }
  };

  const handleUpdateLocation = async () => {
    const isValid = validateLocationFields();
    if (!isValid) return;

    if (!modal.data) return;

    const payload = {
      customerId: customerId,
      regionID: locationFormData.region.value,
      cityID: locationFormData.city.value,
      outletName: locationFormData.outlet.value,
      address: locationFormData.address.value,
      contactName: locationFormData.name.value,
      ContactNo: locationFormData.mobile.value,
      email: locationFormData.email.value,
      alternateContactName: locationFormData.alt_name.value,
      alternateContactNo: locationFormData.alt_mobile.value,
      alternateEmail: locationFormData.alt_email.value,
      postCode: locationFormData.postCode.value,
      UpdateMapDetails: {
        locationName: modal.data?.mapDetails?.location_name || "",
        formattedAddress: modal.data?.mapDetails?.formattedAddress || "",
        placeID: modal.data?.mapDetails?.placeID || "",
        locationType: modal.data?.mapDetails?.locationType || "",
        updateCoordinates: modal.data?.mapDetails?.coordinates || {},
      },
    };

    setStateMeta({ loader: true, error: "" });
    const res = await editCustomerLocation(modal.data.ID, payload);

    if (res.code === 200) {
      notify("success", res?.message || "Location updated successfully.");
      await refreshLocationsAfterSave();
      setStateMeta({ loader: false, error: "" });
      handleCloseModal();
    } else {
      setStateMeta({ loader: false, error: "" });
      notify("danger", res.message || "Failed to update location");
    }
  };

  const confirmDeleteLocation = async (item) => {
    setStateMeta({ loader: true, error: "" });
    const res = await deleteCustomerLocation(customerId, item.ID);

    if (res.code === 200) {
      notify("success", res?.message || "Location deleted successfully.");
      if (locPageNum !== 1) {
        setLocPageNum(1);
      } else {
        await getCustomerLocationList(dispatch, customerId, locPageNum);
      }
    } else {
      notify("danger", res.message || "Failed to delete location");
    }
    setStateMeta({ loader: false, error: "" });
  };

  const handleLocationDelete = (item) => {
    showAlert({
      title: `Are you sure you want to delete this location?`,
      message: `Location "${item.address}" for ${item.outletName} outlet will be permanently deleted.`,
      type: "warning",
      confirmStyle: "danger",
      cancelStyle: "secondary",
      confirmText: "Yes, Delete",
      cancelText: "Cancel",

      onConfirm: async () => confirmDeleteLocation(item),

      onCancel: () => { },
    });
  };

  const geocodeAddress = (address) => {
    return new Promise((resolve) => {
      if (!window.google || !address) return resolve(null);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const latLng = { lat: location.lat(), lng: location.lng() };

          const details = {
            location_name: results[0].address_components?.[0]?.long_name || "",
            formattedAddress: results[0].formatted_address,
            placeID: results[0].place_id,
            locationType: results[0].types?.[0] || "manual",
            coordinates: latLng,
          };
          // update UI state
          setLocationFormData((prev) => ({
            ...prev,
            mapDetails: details,
          }));

          setMarkerPosition(latLng);
          map?.panTo(latLng);

          resolve(details);
        } else {
          setLocationFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              error: "Invalid address. No matching location found.",
            },
            mapDetails: initialServiceField.mapDetails,
          }));

          setMarkerPosition(null);
          resolve(null);
        }
      });
    });
  };

  const handleMarkerDragEnd = async (event) => {
    const newLatLng = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setMarkerPosition(newLatLng);

    // Reverse geocode to get address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: newLatLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        setLocationFormData((prev) => ({
          ...prev,
          address: { ...prev.address, value: results[0].formatted_address },
          mapDetails: {
            location_name: results[0].address_components?.[0]?.long_name || "",
            formattedAddress: results[0].formatted_address,
            placeID: results[0].place_id,
            locationType: results[0].types?.[0] || "manual",
            coordinates: newLatLng,
          },
        }));
      }
    });
  };

  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
    {
      Header: "Outlet Name",
      accessor: "outletName",
      className: "col-outlet-name",
    },
    {
      Header: "Location",
      accessor: "address",
      className: "col-location",
    },
    {
      Header: "Postal Code",
      accessor: "postCode",
      className: "col-details",
    },
    {
      Header: "Region",
      accessor: "region.name",
      className: "col-details",
    },
    {
      Header: "Contact Details",
      accessor: "contactName",
      className: "col-loc-contact",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { contactName, email, contactNo } = row.original;
        return (
          <div className="">
            <div className="d-flex align-items-center mb-1">
              <i className="fa fa-user mr-2 text-light" /> {contactName}
            </div>
            <div className="d-flex align-items-center mb-1">
              <i className="fa fa-envelope mr-2 text-light" /> {email}
            </div>
            <div className="d-flex align-items-center">
              <i className="fa fa-phone mr-2 text-light" /> {contactNo}
            </div>
          </div>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      className: "col-action text-center",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="actions-right d-flex justify-content-start">
          <Button
            className="btn-icon icon-info"
            color="link"
            id={`edit${row.original.ID}`}
            size="sm"
            onClick={async (e) => {
              e.stopPropagation();
              await handleOpenEditLocation(row.original);
            }}
          >
            <i className="tim-icons icon-pencil tim-md" />
          </Button>
          <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
            Edit
          </UncontrolledTooltip>
          <span className="mx-1 mt-1 text-muted">|</span>
          <Button
            className="btn-icon icon-danger"
            color="link"
            id={`reset${row.original.ID}`}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleLocationDelete(row.original);
            }}
          >
            <i className="tim-icons icon-trash-simple tim-md" />
          </Button>
          <UncontrolledTooltip delay={0} target={`reset${row.original.ID}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-end mb-1">
        <Button
          color="default"
          size="sm"
          onClick={() => handleOpenModal("Add", null)}
        >
          <i className="fa fa-plus mr-2" />
          Add Location
        </Button>
      </div>
      <div className="d-flex align-items-center">
        <div className="d-flex align-items-center filter-group flex-nowrap w-100">
          <Input
            type="text"
            placeholder="Search by outlet, location or contact details"
            className="mr-2 mb-2 filter-input-large"
            value={locationFilters.searchText}
            onChange={(e) =>
              setLocationFilters((prev) => ({
                ...prev,
                searchText: e.target.value,
              }))
            }
            autoComplete="off"
            onKeyDown={(e) => e.key === "Enter" && handleLocationSearch()}
            maxLength={200}
          />

          <Select
            className="mr-2 mb-2 filter-select"
            options={regionOptions}
            placeholder="Region"
            classNamePrefix="react-select"
            styles={fixedWidthCustomStyle()}
            value={locationFilters.region}
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleLocationSearch();
              }
            }}
            onChange={(selected) => {
              setLocationFilters((prev) => ({
                ...prev,
                region: selected,
                city: null,
              }));
            }}
          />

          <Button
            color="default"
            className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
            onClick={handleLocationSearch}
          >
            {stateMeta.showLoaderOnClick ? (
              <i className="fa fa-spinner fa-spin search-loader" />
            ) : (
              <>
                <i className="fa fa-search mr-1" />
                Search
              </>
            )}
          </Button>

          {(locationFilters.filterParams.filters?.length ||
            locationFilters.filterParams.searchString) && (
              <Button
                color="default"
                className="mb-2 mt-0 filter-btn btn-simple"
                onClick={handleLocationClear}
              >
                <i className="fa fa-times mr-1" /> Clear
              </Button>
            )}
        </div>
      </div>

      <div className="mt-2 mb-3 mx-1">
        {totalRecords > 0 &&
          (!(
            locationFilters.filterParams?.filters?.length ||
            locationFilters.filterParams?.searchString
          ) ? (
            <span className="font-weight-bold">
              Total Service Locations : {totalRecords}
            </span>
          ) : (
            <span className="font-weight-bold">
              Showing {totalLocationRecords} of {totalRecords} Service Locations
            </span>
          ))}
      </div>

      {stateMeta.isLoading ? (
        <div className="loaderContainer">
          <i className="fa fa-spinner fa-spin fa-2xl loader" />
        </div>
      ) : stateMeta.error ? (
        <div className="loaderContainer text-danger">{stateMeta.error}</div>
      ) : stateMeta.noRecords ? (
        <div className="loaderContainer text-muted">
          No service locations added yet.
        </div>
      ) : (
        <CommonTable
          data={formattedLocationList}
          resizable={false}
          columns={columns}
          defaultPageSize={25}
          showPaginationBottom={true}
          activePage={locPageNum}
          setPageNum={setLocPageNum}
          totalPages={Math.ceil(
            totalLocationRecords / DEFAULT_RECORDS_PER_PAGE,
          )}
          className="-striped -highlight table-hover"
          rowClick={(rowData) => {
            const url = `/admin/customers/${urlEncodedId(customerId)}/details/location-details/${urlEncodedId(rowData.ID)}`;
            window.open(url, "_blank");
          }}
        />
      )}

      <RightPanelCard isOpen={modal.isOpen} onClose={handleCloseModal}>
        <Card className="location-card mb-0 d-flex flex-column">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
            tag="h4"
          >
            {modal.mode} Location
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleCloseModal}
            />
          </CardHeader>

          <CardBody className="mt-2 overflow-auto flex-grow-1">
            <Row>
              {modal.mode !== "View" && (
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${locationFormData.outlet.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Outlet Name<span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="outlet"
                      type="text"
                      placeholder="Enter outlet name"
                      value={locationFormData.outlet.value}
                      onChange={handleLocationChange}
                      onBlur={(e) =>
                        handleLocationBlur(e.target.name, e.target.value)
                      }
                      maxLength={100}
                    />

                    {locationFormData.outlet.error && (
                      <small className="validation-error">
                        {locationFormData.outlet.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              )}

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${locationFormData.name.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Contact Name{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.name.value}
                    </span>
                  ) : (
                    <Input
                      name="name"
                      type="name"
                      placeholder="Enter contact name"
                      value={locationFormData.name.value}
                      onChange={handleLocationChange}
                      onBlur={(e) =>
                        handleLocationBlur(e.target.name, e.target.value)
                      }
                      maxLength={100}
                    />
                  )}
                  {locationFormData.name.error && (
                    <small className="validation-error">
                      {locationFormData.name.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup
                  className={`has-label ${locationFormData.email.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Email Address{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.email.value}
                    </span>
                  ) : (
                    <Input
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={locationFormData.email.value}
                      onChange={handleLocationChange}
                      onBlur={(e) =>
                        handleLocationBlur(e.target.name, e.target.value)
                      }
                      maxLength={100}
                    />
                  )}
                  {locationFormData.email.error && (
                    <small className="validation-error">
                      {locationFormData.email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup
                  className={`has-label ${locationFormData.mobile.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Contact Number{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.mobile.value}
                    </span>
                  ) : (
                    <InputGroup className="input-group">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText className="d-flex align-items-center">
                          <span className="mr-1">🇸🇦</span> +966
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        name="mobile"
                        type="tel"
                        placeholder="Enter contact number"
                        autoComplete="off"
                        value={locationFormData.mobile.value}
                        onChange={handleLocationChange}
                        maxLength={9}
                        onBlur={(e) =>
                          handleLocationBlur(e.target.name, e.target.value)
                        }
                      />
                    </InputGroup>
                  )}
                  {locationFormData.mobile.error && (
                    <small className="validation-error">
                      {locationFormData.mobile.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <hr className="mb-4" />
            <Row>
              <Col md="6" className="mb-3">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>Alternative Contact Name</Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.alt_name.value || "-"}
                    </span>
                  ) : (
                    <Input
                      name="alt_name"
                      type="name"
                      placeholder="Enter alternative contact name"
                      value={locationFormData.alt_name.value}
                      onChange={handleLocationChange}
                      maxLength={100}
                    />
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>Alternative Email Address</Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.alt_email.value || "-"}
                    </span>
                  ) : (
                    <Input
                      name="alt_email"
                      type="email"
                      placeholder="Enter alternative email address"
                      value={locationFormData.alt_email.value}
                      onChange={handleLocationChange}
                      maxLength={100}
                      onBlur={(e) =>
                        handleLocationBlur(e.target.name, e.target.value)
                      }
                    />
                  )}
                  {locationFormData.alt_email.error && (
                    <small className="validation-error">
                      {locationFormData.alt_email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-2">
                <FormGroup className="d-flex flex-column align-items-start">
                  <Label>Alternative Contact Number</Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.alt_mobile.value || "-"}
                    </span>
                  ) : (
                    <InputGroup className="input-group">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText className="d-flex align-items-center">
                          <span className="mr-1">🇸🇦</span> +966
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        name="alt_mobile"
                        type="tel"
                        placeholder="Enter alternative contact number"
                        autoComplete="off"
                        value={locationFormData.alt_mobile.value}
                        onChange={handleLocationChange}
                        maxLength={9}
                        onBlur={(e) =>
                          handleLocationBlur(e.target.name, e.target.value)
                        }
                      />
                    </InputGroup>
                  )}
                  {locationFormData.alt_mobile.error && (
                    <small className="validation-error">
                      {locationFormData.alt_mobile.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <hr className="mb-4" />

            <Row>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${locationFormData.region.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Region{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.region.value}
                    </span>
                  ) : (
                    <Select
                      className={`text-left ${locationFormData.region.error ? "is-invalid" : ""
                        }`}
                      options={regionOptions}
                      placeholder="Select region"
                      classNamePrefix="react-select"
                      styles={customStyle}
                      value={regionOptions.find(
                        (option) =>
                          option.value === locationFormData.region.value,
                      ) || null}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "region")
                      }
                      onBlur={() =>
                        handleLocationBlur(
                          "region",
                          locationFormData.region.value,
                        )
                      }
                    />
                  )}
                  {locationFormData.region.error && (
                    <small className="validation-error">
                      {locationFormData.region.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${locationFormData.city.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    City{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.city.value}
                    </span>
                  ) : (
                    <Select
                      className={`text-left ${locationFormData.city.error ? "is-invalid" : ""
                        }`}
                      options={cityOptions}
                      placeholder="Select city"
                      classNamePrefix="react-select"
                      styles={customStyle}
                      value={cityOptions.find(
                        (option) =>
                          option.value === locationFormData.city.value,
                      ) || null}
                      onChange={(selectedOption) =>
                        handleSelectChange(selectedOption, "city")
                      }
                      isDisabled={!locationFormData.region.value}
                      onBlur={() =>
                        handleLocationBlur("city", locationFormData.city.value)
                      }
                    />
                  )}
                  {locationFormData.city.error && (
                    <small className="validation-error">
                      {locationFormData.city.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6" className="mb-3">
                <FormGroup
                  className={`has-label ${locationFormData.postCode.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Postal Code{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.postCode.value || "-"}
                    </span>
                  ) : (
                    <Input
                      name="postCode"
                      type="name"
                      placeholder="Enter postal code"
                      value={locationFormData.postCode.value}
                      onChange={handleLocationChange}
                      maxLength={10}
                      onBlur={() =>
                        handleLocationBlur("postCode", locationFormData.postCode.value)
                      }
                    />
                  )}
                  {locationFormData.postCode.error && (
                    <small className="validation-error">
                      {locationFormData.postCode.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="12" className="mb-3">
                <FormGroup
                  className={`has-label ${locationFormData.address.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Address{" "}
                    {modal.mode !== "View" && (
                      <span className="validation-error">*</span>
                    )}
                  </Label>
                  {modal.mode === "View" ? (
                    <span className="small-text">
                      {locationFormData.address.value}
                    </span>
                  ) : (
                    <Input
                      name="address"
                      type="text"
                      placeholder="Enter address"
                      value={locationFormData.address.value}
                      onChange={handleLocationChange}
                      onBlur={(e) => {
                        handleLocationBlur(e.target.name, e.target.value);
                        if (e.target.value.trim()) {
                          geocodeAddress(e.target.value.trim());
                        }
                      }}
                      onKeyDown={handleAddressKeyDown}
                      maxLength={100}
                    />
                  )}
                  {locationFormData.address.error && (
                    <small className="validation-error">
                      {locationFormData.address.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col>
                {isLoaded && (
                  <GoogleMap
                    mapContainerClassName="map-container"
                    zoom={markerPosition ? 15 : 4}
                    center={markerPosition || defaultCenter}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                    options={{
                      streetViewControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {markerPosition && (
                      <Marker
                        position={markerPosition}
                        draggable={modal.mode !== "View"}
                        onDragEnd={
                          modal.mode !== "View"
                            ? handleMarkerDragEnd
                            : undefined
                        }
                      />
                    )}
                  </GoogleMap>
                )}
              </Col>
            </Row>
          </CardBody>

          {modal.mode === "View" ? null : (
            <CardFooter className="d-flex justify-content-end mb-0 flex-grow-0">
              <Button
                color="secondary"
                onClick={handleCloseModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="default"
                onClick={
                  modal.mode === "Edit"
                    ? handleUpdateLocation
                    : handleSaveLocation
                }
                disabled={hasAnyError(locationFormData)}
              >
                {stateMeta.loader ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          )}
        </Card>
      </RightPanelCard>
    </>
  );
}

export default ServiceLocations;
